import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, X, XCircle, Eye, EyeOff } from "lucide-react";
import { authService } from "@b2b/services";
import { Button, AuthHeroPanel, BizcoverIndiaBrandMark } from "@b2b/ui-components";
import FormInput from "@b2b/ui-components/features/Registration/components/FormInput";

interface Toast {
  type: "error" | "success";
  message: string;
}

function getServerErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const e = error as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
    name?: string;
  };
  const raw = e.response?.data?.message;
  if (raw != null) {
    return Array.isArray(raw) ? raw.join(" ") : String(raw);
  }
  if (e.name === "ApiError" && typeof e.message === "string" && e.message) {
    return e.message;
  }
  return undefined;
}

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") ?? "").trim(), [searchParams]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setToast(null);
    if (!token) {
      setToast({ type: "error", message: "This reset link is invalid. Request a new one from sign in." });
      return;
    }
    if (password.length < 8) {
      setToast({ type: "error", message: "Password must be at least 8 characters." });
      return;
    }
    if (password !== confirm) {
      setToast({ type: "error", message: "Passwords do not match." });
      return;
    }

    setIsLoading(true);
    try {
      const res = await authService.resetPasswordWithToken(token, password);
      setDone(true);
      setToast({ type: "success", message: res.message });
    } catch (error: unknown) {
      setToast({
        type: "error",
        message: getServerErrorMessage(error) || "Could not reset password. Try requesting a new link.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      <div className="flex flex-col lg:flex-row min-h-screen">
        <div className="hidden lg:flex lg:min-h-screen lg:w-1/2">
          <AuthHeroPanel backgroundImageUrl="https://s.alicdn.com/@img/imgextra/i4/O1CN01IeRuA51ZVC7CKaKE2_!!6000000003199-2-tps-1920-1600.png" />
        </div>

        <div className="flex w-full flex-1 flex-col items-center justify-center overflow-y-auto p-6 md:p-8 lg:w-1/2 lg:justify-center lg:p-12">
          <div className="mx-auto w-full max-w-md flex flex-col min-w-0">
            <div className="lg:hidden mb-6 flex shrink-0 flex-col items-center gap-1 text-center">
              <BizcoverIndiaBrandMark variant="auth" to="/bizcoverindia-home-updated" />
            </div>

            <div className="rounded-2xl border border-[#EEF0F3] bg-white shadow-[0_10px_40px_-12px_rgba(0,0,0,0.12)] shrink-0 p-6 sm:p-10">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#f15a11] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15a11]/40 rounded-sm"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden />
                Back to sign in
              </button>

              <h2 className="text-xl font-bold text-gray-900 mb-2">Set a new password</h2>
              <p className="text-sm text-gray-600 mb-6">Choose a strong password you have not used here before.</p>

              {toast && (
                <div
                  className={`flex items-center gap-2 rounded-lg p-4 mb-6 ${
                    toast.type === "error"
                      ? "bg-red-50 border border-red-200 text-red-800"
                      : "bg-green-50 border border-green-200 text-green-800"
                  }`}
                >
                  <XCircle
                    className={`w-5 h-5 flex-shrink-0 ${toast.type === "error" ? "text-red-600" : "text-green-600"}`}
                  />
                  <p className="flex-1 text-sm font-medium">{toast.message}</p>
                  <button
                    type="button"
                    onClick={() => setToast(null)}
                    className="ml-2 hover:opacity-80 flex-shrink-0"
                    aria-label="Close notification"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {!token ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">This page needs a valid reset link from your email.</p>
                  <Link
                    to="/forgot-password"
                    className="inline-flex text-sm font-bold text-[#f15a11] hover:underline"
                  >
                    Request a new link
                  </Link>
                </div>
              ) : done ? (
                <div className="space-y-4">
                  <Button
                    type="button"
                    variant="ctaPrimary"
                    size="lg"
                    className="h-12 w-full rounded-xl bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
                    onClick={() => navigate("/login", { replace: true })}
                  >
                    Sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <FormInput
                      id="reset-password"
                      label="New password"
                      icon={Lock}
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      minLength={8}
                      visualVariant="authSignin"
                      endAdornment={
                        <button
                          type="button"
                          className="rounded p-1 text-gray-400 hover:text-[#f15a11] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15a11]/40 focus-visible:ring-offset-0 disabled:opacity-50"
                          onClick={() => setShowPassword((p) => !p)}
                          disabled={isLoading}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 shrink-0" aria-hidden />
                          ) : (
                            <Eye className="h-4 w-4 shrink-0" aria-hidden />
                          )}
                        </button>
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FormInput
                      id="reset-password-confirm"
                      label="Confirm password"
                      icon={Lock}
                      type={showConfirm ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Repeat password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      disabled={isLoading}
                      minLength={8}
                      visualVariant="authSignin"
                      endAdornment={
                        <button
                          type="button"
                          className="rounded p-1 text-gray-400 hover:text-[#f15a11] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f15a11]/40 focus-visible:ring-offset-0 disabled:opacity-50"
                          onClick={() => setShowConfirm((p) => !p)}
                          disabled={isLoading}
                          aria-label={showConfirm ? "Hide password" : "Show password"}
                        >
                          {showConfirm ? (
                            <EyeOff className="h-4 w-4 shrink-0" aria-hidden />
                          ) : (
                            <Eye className="h-4 w-4 shrink-0" aria-hidden />
                          )}
                        </button>
                      }
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="ctaPrimary"
                    size="lg"
                    className="h-12 w-full rounded-xl bg-[#f15a11] font-bold shadow-md hover:bg-[#e04d0a]"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Updating…
                      </div>
                    ) : (
                      "Update password"
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
